import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, catchError, EMPTY, filter, map, Observable, retry, Subject, takeUntil, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ReportCategory } from '../../insights/administration/reports/reports.component';
import { AuditService } from './audit.service';
import { EmbedconfigService, IEmbedConfig } from './embedconfig.service';
import { IDeleteReport, IExternalUrl, IExternalUrlsResponse, IModelBase, IReport } from './report.service';
import { SessionStorageService } from './storage.service';
import { UserService } from './user.service';

export interface IDeleteExternalUrl extends IModelBase {
	isDeleted?: boolean;
	name?: string;
}

export interface IUpdateExternalUrl extends IModelBase {
	isDeleted?: boolean;
	name?: string;
	url?: string;
	categoryId?: string;
}

export interface IUserExternalUrlRelation {
	userId: string;
	externalUrlId: string;
}

export interface IUserExternalUrlRelations {
	userId: string;
	userExternalUrls: IUserExternalUrlRelation[];
}

@Injectable({
	providedIn: 'root',
})
export class ExternalUrlService implements OnDestroy, OnInit {
	private externalUrlsSubject$: BehaviorSubject<IExternalUrl[]>;
	public externalUrls$: Observable<IExternalUrl[]>;
	private externalUrlSubject$: BehaviorSubject<IExternalUrl | null>;
	public externalUrl$: Observable<IExternalUrl | null>;
	private externalUrlCategoriesSubject$: BehaviorSubject<ReportCategory[]>;
	public externalUrlCategories$: Observable<ReportCategory[]>;
	unsubscribe: Subject<void> = new Subject<void>();
	externalUrlCategories: ReportCategory[] = [];
	private userExternalUrlsSubject$: BehaviorSubject<IExternalUrl[]>;
	public userExternalUrls$: Observable<IExternalUrl[]>;

	private requestQueue: Subject<Observable<IEmbedConfig>> = new Subject(); // cjs
	private requestQueue$: Observable<Observable<IEmbedConfig>> = this.requestQueue.asObservable();

	constructor(
		private http: HttpClient,
		private embedService: EmbedconfigService,
		private userService: UserService,
		private auditService: AuditService,
		private sessionStorageService: SessionStorageService,
	) {
		this.externalUrlsSubject$ = new BehaviorSubject<IExternalUrl[]>([]);
		this.externalUrls$ = this.externalUrlsSubject$.asObservable();

		this.userExternalUrlsSubject$ = new BehaviorSubject<IExternalUrl[]>([]);
		this.userExternalUrls$ = this.userExternalUrlsSubject$.asObservable();

		this.externalUrlSubject$ = new BehaviorSubject<IExternalUrl | null>(null);
		this.externalUrl$ = this.externalUrlSubject$.asObservable();

		this.externalUrlCategoriesSubject$ = new BehaviorSubject<ReportCategory[]>([]);
		this.externalUrlCategories$ = this.externalUrlCategoriesSubject$.asObservable();

		// If only one report, use that report
		this.externalUrls$
			.pipe(
				takeUntil(this.unsubscribe),
				filter((v) => v.length === 1),
			)
			.subscribe((v) => {
				this.externalUrl = v[0];
			});

		this.userService.accountChanged$.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
			this.externalUrlCategories = [];
			this.externalUrlsSubject$.next([]);
			this.userExternalUrlsSubject$.next([]);
			this.externalUrlSubject$.next(null);
			this.externalUrlCategoriesSubject$.next([]);
		});
	}

	ngOnInit() {}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	public get externalUrl(): IExternalUrl | null {
		return this.externalUrlSubject$.value || null;
	}

	public set externalUrl(v: IExternalUrl | null) {
		this.externalUrlSubject$.next(v);

		if (v) {
			this.sessionStorageService.set('User.Report', JSON.stringify(v));
		} else {
			this.sessionStorageService.remove('User.Report');
		}
	}

	public set userExternalUrls(v: IExternalUrl[] | null) {
		this.userExternalUrlsSubject$.next(v);
	}

	public get userExternalUrls() {
		return this.userExternalUrlsSubject$.value;
	}

	public fetchUserExternalUrlsById(id: string): Observable<IExternalUrlsResponse> {
		console.log('fetching external urls for user...');
		const account = this.userService.account;
		const headers = {
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
			'x-account-id': account.id,
		};
		return this.http.get<IExternalUrlsResponse>(`${environment.awsEnvUri}/insights/users/${id}/externalurls?activeOnly=true`, { headers }).pipe(
			map((val: IExternalUrlsResponse) => {
				const mapped: IExternalUrlsResponse = {
					result: val.result,
				};

				mapped.result.forEach((x) => (x.url = decodeURIComponent(x.url)));
				return mapped;
			}),
			retry(1),
			catchError(this.handleError),
		);
	}

	public fetchExternalUrls(activeOnly: boolean = false): Observable<IExternalUrlsResponse> {
		const account = this.userService.account;
		if (!account) {
			return EMPTY;
		}
		let activeExtUrlsOnly: string = '';
		if (activeOnly) {
			activeExtUrlsOnly = '?activeOnly=true';
		}
		const headers = {
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
			'x-account-id': account.id,
		};
		return this.http
			.get<IExternalUrlsResponse>(`${environment.awsEnvUri}/insights/externalurls${activeExtUrlsOnly}`, {
				headers,
			})
			.pipe(
				map((val: IExternalUrlsResponse) => {
					const mapped: IExternalUrlsResponse = {
						result: val.result,
					};

					mapped.result.forEach((x) => (x.url = decodeURIComponent(x.url)));
					return mapped;
				}),
				retry(1),
				catchError(this.handleError),
			);
	}

	public addUserExternalUrls(userExternalUrlRelations: IUserExternalUrlRelations): Observable<IExternalUrlsResponse> {
		const account = this.userService.account;
		const headers = {
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
			'x-account-id': account.id,
		};
		const userId = this.sessionStorageService.get('User.IAM.Id');
		console.log(JSON.stringify(userExternalUrlRelations, null, 2));
		return this.http.post<IExternalUrlsResponse>(`${environment.awsEnvUri}/insights/users/${userId}/externalurls`, userExternalUrlRelations, { headers }).pipe(retry(1), catchError(this.handleError));
	}

	public updateExternalUrl(externalUrl: IReport): Observable<IExternalUrlsResponse> {
		const body: IUpdateExternalUrl = {
			name: externalUrl.name,
			categoryId: externalUrl.categoryId,
			isDeleted: externalUrl.isDeleted,
			id: externalUrl.id,
			url: externalUrl.url,
		};
		return this.http
			.put(`${environment.awsEnvUri}/insights/externalurls`, body, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	public deleteExternalUrls(deleteExtUrls: IDeleteReport[]): Observable<IExternalUrlsResponse> {
		const account = this.userService.account;
		const headers = {
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
			'x-account-id': account.id,
		};
		return this.http.patch<IExternalUrlsResponse>(`${environment.awsEnvUri}/insights/externalurls`, deleteExtUrls, { headers }).pipe(retry(1), catchError(this.handleError));
	}

	public permanentlyDeleteExternalUrl(externalUrl: IReport): Observable<string> {
		const account = this.userService.account;
		const headers = {
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
			'x-account-id': account.id,
		};
		return this.http
			.delete<string>(`${environment.awsEnvUri}/insights/externalurls/${externalUrl.id}`, {
				headers,
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	public addExternalUrl(name: string, url: string): Observable<any> {
		const body = { name, url };
		return this.http
			.post(`${environment.awsEnvUri}/insights/externalurls`, body, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	private handleError(error: { error: { message: string }; status: any; message: any }) {
		console.log('http error', error);
		let errorMessage = '';
		if (error.error instanceof ErrorEvent) {
			// Get client-side error
			errorMessage = error.error.message;
		} else {
			// Get server-side error
			errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
		}
		if (error.status !== 403) {
			console.log(errorMessage);
			return throwError(() => new Error(errorMessage));
		}
		return [];
	}

	private getHeaders() {
		const account = this.userService.account;
		return {
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
			'x-account-id': account.id,
		};
	}
}
